//@flow

import React, { useEffect, useState, useContext } from "react";
import classnames from "classnames";
import { useFormState, useField } from "react-final-form";
import { OnChange } from "react-final-form-listeners";

import { Row, Label, HiddenInput } from "../../shared/CommonComponents";
import { readAndReplace } from "modules/ShortForm/misc/utilities";

import ShortFormSharedContext from "../../../misc/context";

import Provider from "./Provider";
import Tariff from "./Tariff";

import { useFetchProvidersWithTariffs } from "../../../misc/hooks";

import {
  convertToProvidersOptions,
  convertToTariffsOptions,
  getCategoryId,
  getCompanyName,
  getTariffName,
  getCategoryProviderId,
  getCategoryPermanentTariffId,
  extractDefaultProvider,
  extractDefaultTariff,
} from "./helpers";

const ProvidersAndTariffsHandler = () => {
  const {
    category: { categoryType },
    inheritedStyles: { formRow, formRowPaired, formLabelLight },
    configurationData: {
      translations: { fieldLabels, glossary },
    },
  } = useContext(ShortFormSharedContext);

  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [defaultValues, setDefaultValues] = useState({ defaultProviderId: null, defaultTariffId: null });

  /* Extract previous steps values and pre-selected values from the form */
  const {
    values: { locationId, postalcode, propertyType, yearlyConsumption },
  } = useFormState();

  const providerField = useField("companyId");
  const tariffField = useField("tariffId");

  const apiCallArguments = {
    postalcode,
    categoryType,
    locationId,
    yearlyConsumption,
    isOffice: propertyType === 3,
  };

  const { loading, error, data } = useFetchProvidersWithTariffs(apiCallArguments);

  if (error) console.error(error);

  useEffect(() => {
    setSelectedCompanyId(providerField.input.value);
  }, [providerField.input.value]);

  const getDefaultValues = (data) => {
    const defaultProvider = extractDefaultProvider(data) || [];
    const defaultTariff = extractDefaultTariff(defaultProvider[0] || []);
    return {
      defaultProviderId: defaultProvider?.[0]?.company?.externalId || null,
      defaultTariffId: defaultTariff?.[0]?.id || null,
    };
  };

  useEffect(() => {
    const { defaultProviderId, defaultTariffId } = getDefaultValues(data);
    if (defaultProviderId && defaultTariffId && !selectedCompanyId) {
      setDefaultValues({ defaultProviderId, defaultTariffId });
      providerField.input.onChange(defaultProviderId);
      tariffField.input.onChange(defaultTariffId);
    }
  }, [data]);

  const isPreselectDefaultValuesCase = defaultValues?.defaultTariffId && !selectedCompanyId;

  return (
    <>
      <Row className={classnames(formRow, formRowPaired)}>
        <Provider options={convertToProvidersOptions(data)} loading={loading} data={data} />
      </Row>
      <Row className={classnames(formRow, formRowPaired)}>
        <Label className={formLabelLight}>
          {readAndReplace(fieldLabels.providerAndTariff.tariffId, {
            replace: (domNode) => {
              if (domNode.attribs && domNode.attribs.id === "replace") {
                return <span>{categoryType && glossary[categoryType.toLowerCase()].trans}</span>;
              }
            },
          })}
        </Label>
        <Tariff options={convertToTariffsOptions(data, selectedCompanyId)} loading={loading} />
      </Row>
      <OnChange name={"companyId"}>
        {(companyId) => {
          // This condition prevents the tariff options of being flushed when we change the provider input for the preselected default one in the first render
          if (!isPreselectDefaultValuesCase) tariffField.input.onChange(null);
          setSelectedCompanyId(companyId);
        }}
      </OnChange>
      <HiddenInput name="categoryId" val={getCategoryId(data, selectedCompanyId)} />
      <HiddenInput name="companyName" val={getCompanyName(data, selectedCompanyId)} />
      <HiddenInput name="tariffName" val={getTariffName(data, selectedCompanyId, tariffField.input.value)} />
      <HiddenInput name="categoryProviderId" val={getCategoryProviderId(data, selectedCompanyId)} />
      <HiddenInput
        name="permanentTariffId"
        val={getCategoryPermanentTariffId(data, selectedCompanyId, tariffField.input.value)}
      />
    </>
  );
};

export default ProvidersAndTariffsHandler;
